import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  CommunicationLanguage,
  ObjectTypeEnum,
  type ViewCommunicationItem,
} from "@/briefpoint-client";
import type { Interrogatory } from "@/briefpoint-client/models/Interrogatory";
import {
  useCaseApi,
  useCommunicationApi,
  useDocumentApi,
} from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import {
  caseGetOptions,
  getCommunicationsForObjectOptions,
  getDocumentOptions,
} from "@/lib/query-options";
import type { DocumentDetailClient } from "@/lib/types";
import { addUploadDate, normalizeError } from "@/lib/utils";
import { Modal } from "../Modals";
import InterrogatoriesComboBox from "../Select/InterrogatoriesComboBox";
import { Textarea } from "../ui/textarea";
import { toast } from "../ui/toast";
import styles from "./AddEditClientTaskModal.module.scss";

export default function AddEditClientTaskModal({
  interrogatories,
  communicationId,
  externalCommunication,
  language,
}: {
  interrogatories: Interrogatory[];
  communicationId: string;
  externalCommunication?: ViewCommunicationItem;
  language: CommunicationLanguage;
}) {
  const { documentId } = useParams<{ documentId: string }>();

  const documentApi = useDocumentApi();
  const caseApi = useCaseApi();
  const auth = useAuth();

  const queryClient = useQueryClient();
  const documentQuery = useQuery({
    ...getDocumentOptions(documentId, documentApi),
    enabled: !!documentId,
    select: (data) => addUploadDate(data) as DocumentDetailClient,
  });

  const caseQuery = useQuery({
    ...caseGetOptions(
      caseApi,
      auth.user?.firmId ?? undefined,
      documentQuery.data?.caseId,
    ),
    enabled: !!documentQuery.data?.caseId && !!auth.user?.firmId,
  });

  const location = useLocation();
  const communicationApi = useCommunicationApi();
  const searchParams = new URLSearchParams(location.search);
  const show = searchParams.get("add_task") === "true";
  const history = useHistory();
  const [selectedInterrogatories, setSelectedInterrogatories] = useState<
    number[]
  >([]);
  const [taskDescription, setTaskDescription] = useState("");

  function onClose() {
    searchParams.delete("add_task");
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }

  const translateSingleItemMutation = useMutation({
    mutationFn: async (externalCommunicationId: string) => {
      return communicationApi.communicationTranslateCommunicationItem({
        commId: communicationId,
        id: externalCommunicationId,
        toLang: CommunicationLanguage.ES,
      });
    },
    onError: (error) => {
      //silently send to raygun
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
    },
  });

  const addTaskMutation = useMutation({
    mutationFn: async () => {
      return communicationApi.communicationAddCommunicationItem({
        id: communicationId,
        postCommunicationItem: {
          objectType: ObjectTypeEnum.Interrogatory,
          objectId: selectedInterrogatories.join(","),
          communication: taskDescription,
          language: CommunicationLanguage.EN,
          responseInfo: {
            associatedObjectIds: selectedInterrogatories.map((id) =>
              id.toString(),
            ),
          },
        },
      });
    },
    onSuccess: () => {
      toast.success("Task added successfully");
    },
    onError: (error) => {
      toast.error("Failed to add task. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: (data) => {
      onClose();
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
      if (language === CommunicationLanguage.ES) {
        translateSingleItemMutation.mutate(data?.id ?? "");
      }
    },
  });

  const editTaskMutation = useMutation({
    mutationFn: async () => {
      return communicationApi.communicationPutCommunicationItem({
        id: externalCommunication?.id ?? "",
        putCommunicationItem: {
          id: externalCommunication?.id ?? "",
          objectType: externalCommunication?.objectType,
          objectId: externalCommunication?.objectId,
          communication: taskDescription,
          language: externalCommunication?.language,
          responseInfo: {
            associatedObjectIds: selectedInterrogatories.map((id) =>
              id.toString(),
            ),
          },
        },
      });
    },
    onSuccess: () => {
      toast.success("Task updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update task. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: (data) => {
      onClose();
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
      if (language === CommunicationLanguage.ES) {
        translateSingleItemMutation.mutate(data?.id ?? "");
      }
    },
  });

  useEffect(() => {
    if (show) {
      // reset the form when the modal is shown
      setTaskDescription("");
      setSelectedInterrogatories([]);
    }
    if (externalCommunication) {
      // set the form when the modal is shown with an existing task
      setTaskDescription(externalCommunication.communication ?? "");
      setSelectedInterrogatories(
        externalCommunication.responseInfo?.associatedObjectIds?.map((id) =>
          Number(id),
        ) ?? [],
      );
    }
  }, [externalCommunication, show]);

  return (
    <Modal
      size="lg"
      show={show}
      onClose={onClose}
      onConfirm={() =>
        externalCommunication
          ? editTaskMutation.mutate()
          : addTaskMutation.mutate()
      }
      title={externalCommunication ? "Edit Task" : "Add Task"}
      confirmText={externalCommunication ? "Save" : "Add"}
      disableConfirmButton={
        editTaskMutation.isPending ||
        addTaskMutation.isPending ||
        selectedInterrogatories.length === 0 ||
        taskDescription.trim() === ""
      }
      confirming={editTaskMutation.isPending || addTaskMutation.isPending}
      confirmingText="Saving..."
      cancelButtonVariant="outline-primary"
    >
      <Textarea
        rows={3}
        placeholder="What do you want to ask your client?"
        value={taskDescription}
        onChange={(e) => setTaskDescription(e.target.value)}
      />
      <div className={styles.textLabel}>Make Client Uploads Available to</div>
      <InterrogatoriesComboBox
        id="interrogatories"
        interrogatories={interrogatories}
        selected={selectedInterrogatories}
        documentSubType={documentQuery.data?.info?.documentSubType}
        jurisdictionId={caseQuery.data?.jurisdiction}
        onChange={(value) =>
          setSelectedInterrogatories(value?.map((v) => v.value) ?? [])
        }
      />
    </Modal>
  );
}
